import React, { useState } from 'react';
import { Eye, EyeOff, CheckCircle, X } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';

// ── Spinner ────────────────────────────────────────────────────────────────────
function Spinner() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2.5" strokeLinecap="round"
      style={{ animation: 'cp-spin 0.75s linear infinite', flexShrink: 0 }}>
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  );
}

// ── Moon crescent (nhỏ, dùng lại) ─────────────────────────────────────────────
function MoonCrescent({ size = 40 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 52 52" fill="none">
      <defs>
        <radialGradient id="cp-mg" cx="38%" cy="32%" r="65%">
          <stop offset="0%" stopColor="#e8f8ff" />
          <stop offset="45%" stopColor="#b8e8ff" />
          <stop offset="100%" stopColor="#60c8f0" />
        </radialGradient>
        <filter id="cp-glow" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="4" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>
      <circle cx="26" cy="26" r="23" fill="rgba(0,209,255,0.06)" />
      <g filter="url(#cp-glow)">
        <circle cx="26" cy="26" r="18" fill="url(#cp-mg)" />
        <circle cx="33" cy="22" r="14" fill="#0A0A0A" />
      </g>
    </svg>
  );
}

// ── Strength bar ───────────────────────────────────────────────────────────────
function PasswordStrength({ pw }: { pw: string }) {
  const score =
    (pw.length >= 8 ? 1 : 0) +
    (/[A-Z]/.test(pw) ? 1 : 0) +
    (/[0-9]/.test(pw) ? 1 : 0) +
    (/[^A-Za-z0-9]/.test(pw) ? 1 : 0);

  const labels = ['', 'Yếu', 'Trung bình', 'Tốt', 'Mạnh'];
  const colors = ['', '#f87171', '#fbbf24', '#60d394', '#00D1FF'];

  if (!pw) return null;

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', gap: 4, marginBottom: 5 }}>
        {[1, 2, 3, 4].map(i => (
          <div key={i} style={{
            flex: 1, height: 3, borderRadius: 2,
            background: i <= score ? colors[score] : 'rgba(255,255,255,0.1)',
            transition: 'background 0.3s',
          }} />
        ))}
      </div>
      <div style={{ fontSize: 10, color: colors[score] || 'transparent', fontWeight: 600 }}>
        {labels[score]}
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export function ChangePasswordPage({ onDone, isInApp = false }: { onDone?: () => void; isInApp?: boolean }) {
  const [newPw,    setNewPw]    = useState('');
  const [cfPw,     setCfPw]     = useState('');
  const [showPw,   setShowPw]   = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [success,  setSuccess]  = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (newPw.length < 6) { setError('Mật khẩu cần ít nhất 6 ký tự.'); return; }
    if (newPw !== cfPw)   { setError('Mật khẩu xác nhận không khớp.'); return; }

    setLoading(true);
    const { error: err } = await supabase.auth.updateUser({ password: newPw });
    setLoading(false);

    if (err) {
      setError(err.message);
    } else {
      setSuccess(true);
      setTimeout(async () => {
        if (isInApp) {
          // In-app: chỉ đóng modal, không sign out
          onDone?.();
        } else {
          // Recovery session: sign out để xóa token, buộc login lại
          await supabase.auth.signOut();
          onDone?.();
        }
      }, 2000);
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
        .cp-root * { font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif; }
        @keyframes cp-spin   { to { transform: rotate(360deg); } }
        @keyframes cp-fadein   { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes cp-progress { from { width: 0%; } to { width: 100%; } }
        @keyframes cp-moon-pulse {
          0%,100% { filter: drop-shadow(0 0 8px rgba(0,209,255,0.3)) drop-shadow(0 0 24px rgba(0,209,255,0.12)); }
          50%      { filter: drop-shadow(0 0 16px rgba(0,209,255,0.55)) drop-shadow(0 0 48px rgba(0,209,255,0.2)); }
        }
        .cp-btn {
          transition: box-shadow 0.3s ease, transform 0.15s ease, background 0.2s;
        }
        .cp-btn:hover:not(:disabled) {
          box-shadow: 0 0 20px rgba(0,209,255,0.45), 0 8px 32px rgba(0,0,0,0.5);
          transform: translateY(-1px);
        }
        .cp-input {
          background: transparent; border: none;
          border-bottom: 1px solid rgba(255,255,255,0.15);
          outline: none; color: rgba(255,255,255,0.9);
          font-size: 14px; width: 100%;
          padding: 10px 36px 10px 2px; transition: border-color 0.2s;
          caret-color: #00D1FF;
        }
        .cp-input::placeholder { color: rgba(255,255,255,0.25); }
        .cp-input:focus { border-bottom-color: #00D1FF; }
        .cp-input:-webkit-autofill,
        .cp-input:-webkit-autofill:focus {
          -webkit-box-shadow: 0 0 0 1000px transparent inset;
          -webkit-text-fill-color: rgba(255,255,255,0.9) !important;
          transition: background-color 5000s; caret-color: #00D1FF;
        }
      `}</style>

      {/* Full-screen dark overlay */}
      <div className="cp-root" style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backgroundColor: '#0A0A0A',
        padding: '24px',
        paddingTop: 'env(safe-area-inset-top, 24px)',
        paddingBottom: 'env(safe-area-inset-bottom, 24px)',
      }}>
        {/* Ambient glow */}
        <div style={{
          position: 'absolute', top: '35%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 500, height: 500, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(0,209,255,0.05) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />

        {/* Backdrop click to close (in-app only) */}
        {isInApp && !success && (
          <div
            onClick={() => onDone?.()}
            style={{ position: 'absolute', inset: 0, cursor: 'default' }}
          />
        )}

        {/* Card */}
        <div style={{
          width: '100%', maxWidth: 420,
          position: 'relative',
          background: 'rgba(255,255,255,0.04)',
          backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 24,
          boxShadow: '0 32px 80px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.06)',
          padding: '44px 40px 40px',
          animation: 'cp-fadein 0.7s ease',
        }}>
          {/* Close button (in-app) */}
          {isInApp && !success && (
            <button
              onClick={() => onDone?.()}
              style={{
                position: 'absolute', top: 16, right: 16,
                background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 8, cursor: 'pointer',
                color: 'rgba(255,255,255,0.4)', display: 'flex', padding: 6,
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255,255,255,0.8)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255,255,255,0.4)'; }}
            >
              <X size={14} />
            </button>
          )}

          {success ? (
            /* Success state */
            <div style={{ textAlign: 'center', animation: 'cp-fadein 0.5s ease' }}>
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 60, height: 60, borderRadius: '50%', margin: '0 auto 24px',
                background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.3)',
              }}>
                <CheckCircle size={28} color="#10b981" />
              </div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#ffffff', marginBottom: 10 }}>
                Mật khẩu đã cập nhật!
              </div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', lineHeight: 1.7 }}>
                {isInApp ? 'Mật khẩu đã được cập nhật.' : 'Đang chuyển về trang đăng nhập…'}
              </div>
              {/* Progress bar */}
              <div style={{
                marginTop: 28, height: 2, borderRadius: 1,
                background: 'rgba(255,255,255,0.08)', overflow: 'hidden',
              }}>
                <div style={{
                  height: '100%', background: '#10b981', borderRadius: 1,
                  animation: 'cp-progress 2.5s linear forwards',
                }} />
              </div>
            </div>
          ) : (
            /* Form state */
            <>
              {/* Header */}
              <div style={{ textAlign: 'center', marginBottom: 36 }}>
                <div style={{ display: 'inline-flex', marginBottom: 18, animation: 'cp-moon-pulse 4s ease-in-out infinite' }}>
                  <MoonCrescent size={44} />
                </div>
                <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em', color: '#ffffff', marginBottom: 8 }}>
                  Tạo mật khẩu mới
                </div>
                <div style={{ fontSize: 12, fontWeight: 300, color: 'rgba(150,200,230,0.6)', lineHeight: 1.6 }}>
                  Nhập mật khẩu mới cho tài khoản MoBu của bạn
                </div>
              </div>

              {/* Error */}
              {error && (
                <div style={{
                  marginBottom: 22, padding: '11px 16px', borderRadius: 10,
                  background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
                  fontSize: 12, color: '#f87171', lineHeight: 1.55,
                  animation: 'cp-fadein 0.3s ease',
                }}>
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                {/* New password */}
                <div style={{ marginBottom: 28 }}>
                  <label style={{ display: 'block', fontSize: 10, fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.28)', marginBottom: 8 }}>
                    Mật khẩu mới
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      className="cp-input"
                      type={showPw ? 'text' : 'password'}
                      value={newPw}
                      onChange={e => { setNewPw(e.target.value); setError(''); }}
                      placeholder="Ít nhất 6 ký tự"
                      autoComplete="new-password"
                      autoFocus
                      disabled={loading}
                    />
                    <button type="button" onClick={() => setShowPw(v => !v)} tabIndex={-1}
                      style={{
                        position: 'absolute', right: 2, top: '50%', transform: 'translateY(-50%)',
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: 'rgba(255,255,255,0.3)', display: 'flex', padding: 4, transition: 'color 0.15s',
                      }}
                      onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = 'rgba(0,209,255,0.7)'; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255,255,255,0.3)'; }}
                    >
                      {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                  <PasswordStrength pw={newPw} />
                </div>

                {/* Confirm password */}
                <div style={{ marginBottom: 36 }}>
                  <label style={{ display: 'block', fontSize: 10, fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.28)', marginBottom: 8 }}>
                    Xác nhận mật khẩu
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      className="cp-input"
                      type={showPw ? 'text' : 'password'}
                      value={cfPw}
                      onChange={e => { setCfPw(e.target.value); setError(''); }}
                      placeholder="Nhập lại mật khẩu"
                      autoComplete="new-password"
                      disabled={loading}
                    />
                    {cfPw && newPw && cfPw === newPw && (
                      <div style={{ position: 'absolute', right: 2, top: '50%', transform: 'translateY(-50%)' }}>
                        <CheckCircle size={14} color="#10b981" />
                      </div>
                    )}
                  </div>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={loading}
                  className="cp-btn"
                  style={{
                    width: '100%', padding: '14px', borderRadius: 12, border: 'none',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    background: loading
                      ? 'rgba(0,209,255,0.25)'
                      : 'linear-gradient(135deg, #00b4db 0%, #00D1FF 50%, #0099cc 100%)',
                    color: loading ? 'rgba(255,255,255,0.5)' : '#ffffff',
                    fontSize: 14, fontWeight: 600, letterSpacing: '0.04em',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    opacity: loading ? 0.7 : 1, fontFamily: 'inherit',
                  }}
                >
                  {loading ? <><Spinner /> Đang cập nhật…</> : 'Cập nhật mật khẩu'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </>
  );
}
